import { RESUME_PREFIX } from '$lib/global/constants';

import { isTrackingAllowed } from '$lib/global/consent/consentGate';

import set from 'lodash/set';
import { nanoid } from 'nanoid/non-secure';

import { getNowISO } from './helper';
import { safeSessionStorage, safeLocalStorage, safeGetUserTraits } from './trackingStorage';
import { convert } from './v4';
import { identify } from './v4.calls';
import psProperties, { CONTACT_TRAIT_KEYS } from './v4.ps-properties';

// Prefix for storing campaign session IDs in sessionStorage
const CAMPAIGN_SESSION_PREFIX = 'perspective_campaign_session_';

// Tracks whether any non-homepage page has been seen in this session.
// Used to decide if a homepage arrival should regenerate the session.
const SEEN_NON_HOMEPAGE_PREFIX = 'perspective_seen_non_homepage_';

/**
 * How long an interrupted visit may be resumed for.
 *
 * Deliberately the conventional 30-minute analytics session window: long enough to cover an
 * app switch, short enough that the purpose stays "continue this visit" rather than
 * "recognise a returning visitor", which is a different purpose needing a different basis.
 * It also bounds the shared-device case — the window in which a second person deep-linking
 * into the same funnel could inherit the first person's session.
 */
const RESUME_TTL_MS = 30 * 60 * 1000;

interface ResumePointer {
    sessionId: string;
    expiresAt: number;
}

const conversionProperties = CONTACT_TRAIT_KEYS;

export class TrackingSession {
    private sessionId: string;
    private hasSeenAnyNonHomepage: boolean;
    private visitorProperties: Partial<Record<string, string>>;
    private lastIdentifyKey: string | null = null;

    /**
     * Whether this session may be written to sessionStorage.
     *
     * Defaults to on. `TrackingService` turns it off while the visitor has not answered the
     * cookie banner, so the visit is measured from memory alone and nothing lands on their
     * device — see `setPersistenceEnabled`.
     */
    private persistenceEnabled = true;

    /**
     * The campaign this session belongs to, recorded by whichever of `create` / `tryRestore`
     * / `restoreSession` started it. Constant for the object's life, so the methods that run
     * later — `setCurrentPage` above all — do not each have to be handed it.
     */
    private campaignId = '';

    constructor() {
        this.sessionId = '';
        this.hasSeenAnyNonHomepage = false;
        this.visitorProperties = {};
        this.lastIdentifyKey = null;
    }

    /**
     * Turn sessionStorage persistence on or off.
     *
     * While off the session lives only in this page's memory, so a reload cannot recognise it
     * and starts a new visit instead. Call `flushToStorage` when turning it back on to write
     * out whatever the visit has accumulated so far.
     */
    setPersistenceEnabled(enabled: boolean): void {
        this.persistenceEnabled = enabled;
    }

    /** Write the in-memory session out, for when persistence is enabled mid-visit. */
    flushToStorage(campaignId: string): void {
        if (!this.persistenceEnabled || !this.sessionId) return;

        this.campaignId = campaignId;
        safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, this.sessionId);
        this.persistVisitorProperties();
        this.writeResumePointer();

        if (this.hasSeenAnyNonHomepage) {
            safeSessionStorage.setItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`, '1');
        }
    }

    /**
     * Mirror the session id into localStorage, with a fresh expiry.
     *
     * Two gates, and the pointer's mere existence is what they add up to: it is only on the
     * device if the visitor consented, and only once their visit got past the entry page.
     * `peekResume` therefore needs nothing to read back but the id — a pointer that is there
     * is one worth resuming.
     *
     * Not writing before the visit gets underway is the load-bearing half. Someone who
     * glanced at the entry page and came back to a deeper link half an hour later would
     * otherwise be folded into that first visit, costing a `Funnel Loaded` and a Session for
     * a visit that really is new — the funnel's own visit counts are what CAP-4147 and
     * CAP-4420 were about.
     *
     * Refreshed on each page view rather than written once, so the window is measured from
     * the visitor's last activity; a 40-minute funnel would otherwise outlive its own pointer.
     */
    private writeResumePointer(): void {
        if (!this.persistenceEnabled || !this.sessionId || !this.campaignId) return;

        if (!this.hasSeenAnyNonHomepage) return;

        if (!isTrackingAllowed()) return;

        const pointer: ResumePointer = {
            sessionId: this.sessionId,
            expiresAt: Date.now() + RESUME_TTL_MS,
        };

        safeLocalStorage.setItem(`${RESUME_PREFIX}${this.campaignId}`, JSON.stringify(pointer));
    }

    /**
     * The session id of an interrupted visit, if one is still resumable.
     *
     * Returns null without consent, so the entry is never *read* either — access to terminal
     * equipment storage is § 25 TDDDG whether or not anything is written. Expired, malformed
     * and over-long pointers are deleted on sight rather than left to linger, which is what
     * keeps `RESUME_TTL_MS` a real retention limit rather than a comment.
     */
    static peekResume(campaignId: string): string | null {
        if (!isTrackingAllowed()) return null;

        const key = `${RESUME_PREFIX}${campaignId}`;
        const raw = safeLocalStorage.getItem(key);

        if (!raw) return null;

        let parsed: unknown = null;

        try {
            parsed = JSON.parse(raw);
        } catch {
            // Left as null, which the shape check below rejects like any other non-pointer.
        }

        // `JSON.parse('null')` yields null, and destructuring that throws — the stored-null
        // hazard `consentGate.asSelection` documents, which took the funnel down once already.
        // Anything that is not an object is not a pointer, so fall through to the same
        // removal the expiry checks use.
        const { sessionId, expiresAt } = (
            typeof parsed === 'object' && parsed !== null ? parsed : {}
        ) as Partial<ResumePointer>;

        // One reading of the clock, and a positive range test: `NaN` fails both comparisons
        // and the `Infinity` an overflowing exponent parses to fails the upper bound, so
        // neither needs a check of its own. The upper bound earns its place because the value
        // comes off the visitor's device — a corrupted entry, or one written by a build with
        // a longer TTL, must not grant itself more life than `RESUME_TTL_MS`, which is the
        // retention the cookie policy declares.
        const now = Date.now();
        const isValid =
            typeof sessionId === 'string' &&
            sessionId.length > 0 &&
            typeof expiresAt === 'number' &&
            expiresAt > now &&
            expiresAt <= now + RESUME_TTL_MS;

        if (!isValid) {
            safeLocalStorage.removeItem(key);

            return null;
        }

        return sessionId;
    }

    static peek(campaignId: string): { sessionId: string; hasSeenAnyNonHomepage: boolean } | null {
        const sessionId = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!sessionId) return null;

        const hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        return { sessionId, hasSeenAnyNonHomepage };
    }

    static peekBySessionId(sessionId: string): { hasSeenAnyNonHomepage: boolean } | null {
        const traits = safeGetUserTraits(`${sessionId}_user_traits`);

        if (traits === null) return null;

        const hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        return { hasSeenAnyNonHomepage };
    }

    private applyRestoredSession(sessionId: string, traits: string | null): void {
        this.sessionId = sessionId;
        this.hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        if (traits !== null) {
            try {
                const obj = JSON.parse(traits);

                for (const [key, val] of Object.entries(obj)) {
                    if (val !== null && val !== undefined) {
                        this.visitorProperties[key] = String(val);
                    }
                }
            } catch (error) {
                console.error('Failed to parse user traits:', error);
            }
        }

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }
    }

    tryRestore(campaignId: string): boolean {
        const sessionId = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!sessionId) return false;

        this.campaignId = campaignId;

        const traits = safeGetUserTraits(`${sessionId}_user_traits`);
        this.applyRestoredSession(sessionId, traits);
        return true;
    }

    /**
     * Adopt `sessionId` as this session, and point the tab at it.
     *
     * Traits are looked up but not required: a visit resumed from another browsing context
     * lost them with that context, and the backend still holds them against the session id,
     * merging per-field on delivery.
     */
    restoreSession(campaignId: string, sessionId: string): void {
        this.campaignId = campaignId;
        this.applyRestoredSession(sessionId, safeGetUserTraits(`${sessionId}_user_traits`));

        if (this.persistenceEnabled) {
            safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, sessionId);
        }
    }

    /**
     * `restoreSession`, but only for a session this device has traits for.
     *
     * The `?sessionId` branch hands over an id from the URL, so unlike a resume it has no
     * prior evidence the session is this visitor's; traits being present is that evidence.
     */
    tryRestoreFromSessionId(campaignId: string, sessionId: string): boolean {
        if (safeGetUserTraits(`${sessionId}_user_traits`) === null) return false;

        this.restoreSession(campaignId, sessionId);

        return true;
    }

    create(campaignId: string): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        this.campaignId = campaignId;
        this.sessionId = urlSessionId || nanoid();

        // Whatever the previous session left behind is not this one's to resume — a homepage
        // arrival regenerating the session is precisely the signal that the old journey
        // ended. No write here: `setCurrentPage` runs on the very next line of
        // `handleRouteChange` and writes a strictly fresher pointer if this visit earns one.
        safeLocalStorage.removeItem(`${RESUME_PREFIX}${campaignId}`);

        if (this.persistenceEnabled) {
            safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, this.sessionId);
            safeSessionStorage.setItem(`${this.sessionId}_user_traits`, '{}');
        }

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }
    }

    // --- Session ID ---

    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Whether this session should be regenerated on homepage arrival.
     * Returns true when the user has visited any non-homepage page in this session,
     * meaning a homepage arrival signals a new funnel journey.
     */
    shouldRegenerateForHomepage(): boolean {
        if (!this.sessionId) return false;
        return this.hasSeenAnyNonHomepage;
    }

    // Called from Page.svelte after variant resolution to track which page is currently shown.
    setCurrentPage(_pageSlug: string, isHomePage: boolean): void {
        if (!isHomePage && !this.hasSeenAnyNonHomepage) {
            this.hasSeenAnyNonHomepage = true;

            if (this.persistenceEnabled) {
                safeSessionStorage.setItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`, '1');
            }
        }

        // Keep the resume window measured from the visitor's last page view, not from when
        // the visit started.
        this.writeResumePointer();
    }

    // --- Visitor Properties ---

    updateVisitorProperties(
        obj: { [key: string]: unknown },
        options?: { suppressConversion?: boolean },
    ): void {
        Object.keys(obj).forEach((key) => {
            set(this.visitorProperties, key, obj[key]);
        });

        set(this.visitorProperties, psProperties.lastSeenAt, getNowISO());

        // Apply conversion state BEFORE persisting/identifying so a single identify
        // (and the persisted traits) carry ps_converted.
        const justConverted = options?.suppressConversion ? false : this.applyConversionState();

        this.persistVisitorProperties();

        identify(this.visitorProperties);

        // Fire the Session Converted event AFTER identify so the user is identified first.
        if (justConverted) {
            convert();
        }
    }

    // Safety-net persistence of visitor properties to sessionStorage (CON-4201), so state
    // (including ps_converted) survives a reload even if Segment hasn't persisted it yet.
    private persistVisitorProperties(): void {
        if (!this.persistenceEnabled) return;

        if (this.sessionId) {
            safeSessionStorage.setItem(
                `${this.sessionId}_user_traits`,
                JSON.stringify(this.visitorProperties),
            );
        }
    }

    // Sets conversion fields if a conversion newly occurs this call.
    // Returns true when conversion was newly applied; does NOT fire the convert() track event.
    private applyConversionState(): boolean {
        if (this.visitorProperties[psProperties.isConverted]) {
            return false;
        }

        const hasConversionProp = conversionProperties.some(
            (property) => this.visitorProperties[property],
        );

        if (!hasConversionProp) {
            return false;
        }

        set(this.visitorProperties, psProperties.isConverted, true);
        set(this.visitorProperties, psProperties.convertedAt, getNowISO());
        return true;
    }

    triggerDeferredConversion(): void {
        const justConverted = this.applyConversionState();

        this.persistVisitorProperties();

        identify(this.visitorProperties);

        if (justConverted) {
            convert();
        }
    }

    getVisitorProperties(): Partial<Record<string, string>> {
        return this.visitorProperties;
    }

    // --- Identify Deduplication ---

    // Dedup on the stable identity payload only (traits + userId). analytics.js regenerates
    // messageId/timestamp on every call, so comparing the whole payload would never detect
    // two logically-identical identify calls as duplicates.
    isIdentifyDuplicate(data: { traits?: unknown; userId?: unknown }): boolean {
        const key = JSON.stringify({ traits: data.traits ?? null, userId: data.userId ?? null });
        const isEqual = key === this.lastIdentifyKey;

        if (!isEqual) {
            this.lastIdentifyKey = key;
        }
        return isEqual;
    }
}
